/**
 * SEO Utility Functions for GeniusNexa
 * Provides helper functions for SEO optimization across the application
 */

import { baseUrl } from "../config";

/**
 * Generate organization structured data
 */
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "GeniusNexa",
  url: baseUrl,
  logo: `${baseUrl}/images/logo.svg`,
  description:
    "Leading technology solutions provider specializing in AI, cloud computing, and digital transformation",
  foundingDate: "2020",
  email: "info@geniusnexa.com",
  address: {
    "@type": "PostalAddress",
    addressCountry: "US",
    addressLocality: "Your City",
    addressRegion: "Your State",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "Customer Service",
    telephone: "+91 9944544603",
    email: "support@geniusnexa.com",
    availableLanguage: ["English"],
  },
  sameAs: [
    "https://www.linkedin.com/company/geniusnexa",
    "https://twitter.com/geniusnexa",
    "https://www.facebook.com/geniusnexa",
    "https://github.com/geniusnexa",
  ],
});

/**
 * Generate website structured data with search action
 */
export const getWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "GeniusNexa",
  url: baseUrl,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${baseUrl}/search?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

/**
 * Generate breadcrumb structured data
 */
export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

/**
 * Generate article structured data
 */
export const getArticleSchema = ({
  title,
  description,
  image,
  datePublished,
  dateModified,
  author = "GeniusNexa Team",
  url,
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description: description,
  image: image || `${baseUrl}/images/default-og.jpg`,
  datePublished: datePublished,
  dateModified: dateModified || datePublished,
  author: {
    "@type": "Person",
    name: author,
  },
  publisher: {
    "@type": "Organization",
    name: "GeniusNexa",
    logo: {
      "@type": "ImageObject",
      url: `${baseUrl}/images/logo.svg`,
    },
  },
  url: url,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": url,
  },
});

/**
 * Generate service structured data
 */
export const getServiceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: service.name,
  description: service.description,
  provider: {
    "@type": "Organization",
    name: "GeniusNexa",
    url: baseUrl,
  },
  areaServed: "Worldwide",
  serviceType: service.category || "Technology Services",
});

/**
 * Generate FAQ structured data
 */
export const getFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

/**
 * Generate local business structured data
 */
export const getLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "GeniusNexa",
  image: `${baseUrl}/images/logo.svg`,
  "@id": baseUrl,
  url: baseUrl,
  telephone: "+91 9944544603",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Your Street Address",
    addressLocality: "Your City",
    addressRegion: "Your State",
    postalCode: "Your ZIP",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 0.0,
    longitude: 0.0,
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "18:00",
  },
  sameAs: [
    "https://www.linkedin.com/company/geniusnexa",
    "https://twitter.com/geniusnexa",
  ],
});

/**
 * Generate combined structured data for homepage
 */
export const getHomepageSchema = () => ({
  "@context": "https://schema.org",
  "@graph": [
    getOrganizationSchema(),
    getWebsiteSchema(),
    getLocalBusinessSchema(),
  ],
});

/**
 * Clean and optimize meta description
 * @param {string} text - Raw text
 * @param {number} maxLength - Maximum length (default 160)
 */
export const optimizeMetaDescription = (text, maxLength = 160) => {
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.substring(0, maxLength - 3) + "...";
};

/**
 * Generate meta keywords from array
 * @param {Array} keywords - Array of keywords
 */
export const generateKeywords = (keywords) => {
  return keywords.join(", ");
};

/**
 * Generate social media meta tags
 */
export const getSocialMetaTags = ({
  title,
  description,
  image,
  url,
  type = "website",
}) => ({
  // Open Graph
  "og:title": title,
  "og:description": description,
  "og:image": image,
  "og:url": url,
  "og:type": type,
  "og:site_name": "GeniusNexa",

  // Twitter
  "twitter:card": "summary_large_image",
  "twitter:title": title,
  "twitter:description": description,
  "twitter:image": image,
  "twitter:site": "@GeniusNexa",

  // Additional
  author: "GeniusNexa",
  robots: "index, follow",
});

const seoUtils = {
  getOrganizationSchema,
  getWebsiteSchema,
  getBreadcrumbSchema,
  getArticleSchema,
  getServiceSchema,
  getFAQSchema,
  getLocalBusinessSchema,
  getHomepageSchema,
  optimizeMetaDescription,
  generateKeywords,
  getSocialMetaTags,
};

export default seoUtils;
